// Kırılım satır türleri — grid'in saf yardımcıları (spec §6.1 / §18 DR1–DR12). Tür kararı shared
// `effectiveNodeKind` (tek kaynak — grid/portal/prompt/DOCX/Excel aynı kural); hesap grubu shared
// `calculationGroupDecision` (resolve ile aynı NULL kuralı: ≥1 üyeli ilk tam grup). Bileşen yok;
// kpi-grid-nav `buildRows` / `navReducer` buradan okur, kpi-grid-nav bu modülü yeniden dışa aktarır.

import {
  calculationGroupDecision,
  effectiveNodeKind,
  stripParentPrefix,
  type KpiResolvedCell,
  type KpiResolvedIndicator,
} from "@mahir/shared";
import { KPI_BREAKDOWN_COPY, type KpiBreakdownTotalState } from "./kpi-breakdown-copy";

/**
 * Satır türü: `band` kategori (ya da Y4a yapı satırı / eski "metrik + grupsuz çocuk"), `total`
 * kırılımlı gösterge (çocukları yalnız grup), `group` kırılım grubu bandı (hücresiz, ince),
 * `row` yaprak / kırılım üyesi.
 */
export type GridRowKind = "band" | "total" | "group" | "row";

export interface TotalRowInfo {
  /** grup satırları sırasıyla — etiket + sette olan üye sayısı (aria / "· N kırılım") */
  groups: { label: string; members: number }[];
  /** tüm gruplardaki üye satırı sayısı (portal "· N alt kalem") */
  memberCount: number;
  /** hesap grubunun sette olan üyeleri (DR4 sayımı); hesap grubu yoksa null */
  calcMemberCodes: string[] | null;
  /** kırılımdan türetilmiş (hesap grubu var) — yazım kapalı */
  derived: boolean;
}

export interface GroupRowInfo {
  /** bu sette toplamın hesap grubu mu (danışman `· hesap grubu`) */
  calc: boolean;
  /** `complete=false` → "(seçili kırılımlar)" */
  complete: boolean;
}

export interface MemberRowInfo {
  /** ad hücresi `aria-label`: "Toplam çalışan sayısı › Cinsiyete göre › Kadın" */
  path: string;
  /** görünen etiket — üstün etiketi tekrarlanmaz ("Toplam çalışan — Kadın" → "Kadın") */
  label: string;
}

type ChildrenOf = (id: string) => readonly KpiResolvedIndicator[];

export function rowKindOf(
  ind: KpiResolvedIndicator,
  children: readonly KpiResolvedIndicator[],
): GridRowKind {
  // grup bandı her zaman grup (Y4a yapı satırı olsa da — zaten değersiz, aç/kapanmaz)
  const k = effectiveNodeKind(ind, children);
  if (k === "breakdown_group") return "group";
  if (ind.structural) return "band";
  if (k === "total") return "total";
  return k === "category" ? "band" : "row";
}

/**
 * Veri Girişi açılışında kapalı başlayan düğümler (D6): çocuklu kategori/toplam satırları. Grup
 * bantları AÇIK başlar — toplam açılınca üyeler doğrudan görünür (RF-2; iki seviyeli grid öncesi
 * davranış — grup bandı aç/kapa TODO-KPI-124 ile geldi, başlangıç durumu değişmedi).
 */
export function initialCollapsedIds(all: readonly KpiResolvedIndicator[]): Set<string> {
  const byParent = new Map<string, KpiResolvedIndicator[]>();
  for (const i of all) {
    if (i.parentId === null) continue;
    byParent.set(i.parentId, [...(byParent.get(i.parentId) ?? []), i]);
  }
  const out = new Set<string>();
  for (const i of all) {
    const kids = byParent.get(i.id) ?? [];
    if (kids.length > 0 && rowKindOf(i, kids) !== "group") out.add(i.id);
  }
  return out;
}

/**
 * Kategori süzgeci seçenekleri (danışman + portal Veri Girişi): kökteki BANT satırları — kategori,
 * Y4a yapı satırı ya da eski "metrik + grupsuz çocuk". Kökteki kırılımlı toplam ve portal K3'te
 * köke taşınmış grup bandı kategori değildir (spec §6.1). Kök = üstü sette olmayan (buildRows ile aynı).
 */
export function rootCategoryRows(all: readonly KpiResolvedIndicator[]): KpiResolvedIndicator[] {
  const ids = new Set(all.map((i) => i.id));
  const childrenOf: ChildrenOf = (id) => all.filter((i) => i.parentId === id);
  return all.filter((i) => {
    if (i.parentId !== null && ids.has(i.parentId)) return false;
    const kids = childrenOf(i.id);
    return kids.length > 0 && rowKindOf(i, kids) === "band";
  });
}

/** Toplamın hesap grubu (resolve ile aynı karar); grup satırı ya da null. */
export function calcGroupFor(
  total: KpiResolvedIndicator,
  childrenOf: ChildrenOf,
): { derived: boolean; group: KpiResolvedIndicator | null } {
  const groups = childrenOf(total.id).filter((g) => g.nodeKind === "breakdown_group");
  const refs = groups.map((g) => ({
    ...g,
    // açık seçim metrik kimliği taşır → aday kimliği metrik kimliği
    id: g.metricId,
    memberCount: childrenOf(g.id).length,
  }));
  const d = calculationGroupDecision(refs, total.calculationGroupMetricId ?? null);
  return { derived: d.derived, group: groups.find((g) => g.metricId === d.group?.id) ?? null };
}

export function totalRowInfo(total: KpiResolvedIndicator, childrenOf: ChildrenOf): TotalRowInfo {
  const groups = childrenOf(total.id).filter((g) => g.nodeKind === "breakdown_group");
  const calc = calcGroupFor(total, childrenOf);
  return {
    groups: groups.map((g) => ({ label: g.label, members: childrenOf(g.id).length })),
    memberCount: groups.reduce((n, g) => n + childrenOf(g.id).length, 0),
    calcMemberCodes: calc.group ? childrenOf(calc.group.id).map((m) => m.code) : null,
    derived: calc.derived,
  };
}

export function groupRowInfo(
  group: KpiResolvedIndicator,
  parent: KpiResolvedIndicator | undefined,
  childrenOf: ChildrenOf,
): GroupRowInfo {
  const isTotal = !!parent && rowKindOf(parent, childrenOf(parent.id)) === "total";
  return {
    calc: isTotal && calcGroupFor(parent, childrenOf).group?.id === group.id,
    complete: group.breakdown?.complete !== false,
  };
}

/**
 * Grup altındaki üye: yol = toplam › grup › üye. Portal K3'te toplam gizliyse grup kategoriye
 * taşınır ve etiketi "<üst> — <grup>" olur → yol "<üst> — <grup> › üye", önek "<üst>".
 */
export function memberRowInfo(
  member: KpiResolvedIndicator,
  group: KpiResolvedIndicator,
  total: KpiResolvedIndicator | undefined,
  /** üst toplam kendisi bir üyeyse (iki seviye) onun tam yolu — yol köke kadar uzar */
  totalPath?: string,
): MemberRowInfo {
  const sep = KPI_BREAKDOWN_COPY.pathSeparator;
  const parentLabel = total?.label ?? hoistedParentLabel(group.label);
  const label =
    member.dimensionMember && parentLabel
      ? stripParentPrefix(parentLabel, member.label)
      : member.label;
  const head = totalPath ?? total?.label;
  const path = [...(head ? [head] : []), group.label, label].join(sep);
  return { path, label };
}

function hoistedParentLabel(groupLabel: string): string | null {
  // "<üst> — <grup>" ya da tam yol "A — B — <üye-toplam> — <grup>": üyenin önek attığı üst, grup
  // adından hemen önceki parça
  const parts = groupLabel.split(" — ");
  return parts.length >= 2 ? parts[parts.length - 2]! : null;
}

/** Ad hücresi girintisi: 10 + depth × 18 px; dar düzende (≤768) adım 12 px (spec §6.1). */
export function rowIndent(depth: number, narrow: boolean): number {
  return 10 + depth * (narrow ? 12 : 18);
}

/** Kırılım hücre nedenleri (toplamın "hesaplanamıyor" durumları — kırmızı değil). */
const BREAKDOWN_FAILURES: ReadonlySet<string> = new Set([
  "breakdown_group_missing",
  "breakdown_member_missing",
]);

export type TotalCellState =
  | {
      state: "member_missing_in_set";
      inSet: number;
      total: number | null;
      /** TODO-KPI-111: eksik üye adları (danışman DTO'su; portalda yok → boş) */
      missingLabels: string[];
    }
  | { state: "member_value_missing"; entered: number; total: number }
  | { state: Exclude<KpiBreakdownTotalState, "member_missing_in_set" | "member_value_missing"> };

/**
 * DR4 — toplam hücresinin dört durumu. Raporlanan yedek: K20 hücresi kırılım nedeni taşır;
 * üye sette eksik: `breakdown_member_missing` (toplam sayısı yalnız `missingRefs` geldiyse);
 * üye değeri eksik: hesap grubu üyelerinden biri `missingRefs`'te; hesap grubu başka sette:
 * `breakdown_group_missing`. Diğer hatalar (döngü, payda…) normal HESAPLANAMADI kalır → null.
 */
export function totalCellState(
  row: { kind: GridRowKind; total?: TotalRowInfo | undefined },
  cell: KpiResolvedCell | undefined,
): TotalCellState | null {
  if (!cell) return null;
  const failure = cell.failure && BREAKDOWN_FAILURES.has(cell.failure) ? cell.failure : null;
  // Hesap grubu başka sette (3): bu sette grup satırı olmayabilir — toplam yaprak gibi çizilir
  // (`kind: "row"`); kırılım nedeni DTO'dan gelir (resolve `breakdown_group_missing`).
  if (row.kind !== "total" && !(row.kind === "row" && failure === "breakdown_group_missing"))
    return null;
  if (cell.isReported === true && failure) return { state: "reported_fallback" };
  if (cell.status !== "calc_error") return null;
  if (failure === "breakdown_group_missing") return { state: "calc_group_other_set" };
  const members = row.total?.calcMemberCodes ?? [];
  if (failure === "breakdown_member_missing") {
    const missing = cell.missingRefs?.length ?? 0;
    return {
      state: "member_missing_in_set",
      inSet: members.length,
      total: missing > 0 ? members.length + missing : null,
      missingLabels: (cell.missingRefs ?? []).flatMap((code) => {
        const label = cell.missingMemberLabels?.[code];
        return label ? [label] : [];
      }),
    };
  }
  if (cell.failure || members.length === 0) return null;
  const refs = new Set(cell.missingRefs ?? []);
  const missing = members.filter((c) => refs.has(c)).length;
  if (missing === 0) return null;
  return {
    state: "member_value_missing",
    entered: members.length - missing,
    total: members.length,
  };
}

/**
 * Grid hücresinde DR4 durumunun görünümü (hesaplanamayan toplam — asla kırmızı): soluk "—" (+ kısa
 * sayım metni), tam açıklama `title`'da. Danışmanda "başka sette" amber; portalda set/grup adı
 * olmayan tek genel kopya. Raporlanan yedek hücresi sayı taşır → null (normal görünüm + rozet).
 */
export function totalCellView(
  s: TotalCellState,
  client: boolean,
): { text: string; title: string; amber: boolean } | null {
  const c = KPI_BREAKDOWN_COPY.states;
  const pc = KPI_BREAKDOWN_COPY.portal;
  switch (s.state) {
    case "member_missing_in_set":
      if (client) return { text: "—", title: pc.states.member_missing_in_set, amber: false };
      return s.total !== null
        ? {
            text: `— ${c.member_missing_in_set.cell(s.inSet, s.total)}`,
            title: c.member_missing_in_set.cell(s.inSet, s.total),
            amber: false,
          }
        : { text: "—", title: c.member_missing_in_set.cellGeneric, amber: false };
    case "member_value_missing":
      return {
        text: c.member_value_missing.cell(s.entered, s.total),
        title: client ? pc.waitMembers : c.member_value_missing.info,
        amber: false,
      };
    case "calc_group_other_set":
      return client
        ? { text: "—", title: pc.states.calc_group_other_set, amber: false }
        : { text: "—", title: c.calc_group_other_set.cell, amber: true };
    case "reported_fallback":
      return null;
  }
}

const round6 = (v: number): number => Number(v.toFixed(6));

/**
 * DR1 — toplam hücresinde amber nokta: hesap grubu dışı doğrulama grubunun değeri (resolve
 * `breakdownChecks` — yalnız shared `isVerificationGroup` grupları için üretilir) toplamla eşit
 * değil. Onaysız/XXX üst karşılaştırılmaz; `rollup='none'` girdisi (eski DTO) savunma olarak elenir;
 * tolerans 0 (6 ondalık — AI kontrolü `breakdown_mismatch` ile aynı kural).
 */
export function breakdownMismatch(cell: KpiResolvedCell | undefined): boolean {
  if (cell?.status !== "filled" || typeof cell.value !== "number") return false;
  if (cell.unverified === true || cell.hasPending === true) return false;
  const v = round6(cell.value);
  return (cell.breakdownChecks ?? []).some(
    (c) =>
      c.rollup !== "none" &&
      c.status === "filled" &&
      typeof c.value === "number" &&
      round6(c.value) !== v,
  );
}

/**
 * FE yazım kapısı (kpi-store / portal-kpi-store): türetilmiş gösterge, kategori/grup satırı ve
 * hesap gruplu kırılım toplamı yazılmaz — üye kaydı toplamı asla hedeflemez (BE 400
 * `KPI_DERIVED_READONLY` / `KPI_GROUP_READONLY` ile aynı kural).
 */
export function isWriteBlockedIndicator(
  ind: KpiResolvedIndicator,
  all: readonly KpiResolvedIndicator[],
): boolean {
  if (ind.isDerived || ind.nodeKind === "breakdown_group" || ind.nodeKind === "category")
    return true;
  const childrenOf: ChildrenOf = (id) => all.filter((i) => i.parentId === id);
  if (rowKindOf(ind, childrenOf(ind.id)) !== "total") return false;
  return calcGroupFor(ind, childrenOf).derived;
}

/**
 * Kırılımdan türetilmiş toplam (müşteri geneli etkin türetilmişlik): bu sette hesap grubu olan
 * toplam ya da hesap grubu BAŞKA sette olan gösterge (resolve `isDerived:true` +
 * `catalogDerived:false` — grup satırı olmasa da). Formüllü türetilmiş gösterge DEĞİL.
 */
export function isBreakdownDerivedTotal(
  ind: KpiResolvedIndicator,
  all: readonly KpiResolvedIndicator[],
): boolean {
  if (ind.isDerived && ind.catalogDerived === false) return true;
  const childrenOf: ChildrenOf = (id) => all.filter((i) => i.parentId === id);
  return rowKindOf(ind, childrenOf(ind.id)) === "total" && calcGroupFor(ind, childrenOf).derived;
}

/**
 * "Gösterge" sayımı (backend `countMetricIndicators` ile aynı tanım, shared `effectiveNodeKind`):
 * metrik ya da kırılımlı toplam — kategori ve grup düğümü sayılmaz, üyeler sayılır.
 */
export function isMetricIndicator(
  ind: KpiResolvedIndicator,
  children: readonly KpiResolvedIndicator[],
): boolean {
  if (ind.structural) return false;
  const k = effectiveNodeKind(ind, children);
  return k === "metric" || k === "total";
}

/**
 * Kırılım toplamı / grup satırına yazma denemesinin kullanıcı metni (grid ipucu + sunucu
 * `KPI_DERIVED_READONLY` / `KPI_GROUP_READONLY`): danışmanda hesap grubu adıyla, portalda tek genel
 * kopya. Kırılımla ilgisi olmayan satır → null (mevcut davranış).
 */
export function readonlyHintFor(
  ind: KpiResolvedIndicator,
  all: readonly KpiResolvedIndicator[],
  client: boolean,
): string | null {
  const group = ind.nodeKind === "breakdown_group";
  if (!group && !isBreakdownDerivedTotal(ind, all)) return null;
  if (client) return KPI_BREAKDOWN_COPY.portal.autoComputed;
  if (group) return KPI_BREAKDOWN_COPY.readonlyHint(null);
  const childrenOf: ChildrenOf = (id) => all.filter((i) => i.parentId === id);
  return KPI_BREAKDOWN_COPY.readonlyHint(calcGroupFor(ind, childrenOf).group?.label ?? null);
}

/**
 * DR4 bekleme: toplam hücresi, hesap grubu üyesinin AYNI hücresine (işletme × yıl; konsolide /
 * ara toplam kolonunda aynı yılın herhangi bir işletmesi) iyimser kayıt sürerken meşguldür —
 * grid yenilemesi gelene kadar (kpi-store `pendingCells`, anahtar `metricId|entityId|year`).
 */
export function totalRecomputing(
  pending: Readonly<Record<string, number>>,
  total: KpiResolvedIndicator,
  all: readonly KpiResolvedIndicator[],
  col: { entityId: string | null; year: number; aggregate: boolean },
): boolean {
  const keys = Object.keys(pending);
  if (keys.length === 0) return false;
  const childrenOf: ChildrenOf = (id) => all.filter((i) => i.parentId === id);
  if (rowKindOf(total, childrenOf(total.id)) !== "total") return false;
  const calc = calcGroupFor(total, childrenOf).group;
  if (!calc) return false;
  const members = new Set(childrenOf(calc.id).map((m) => m.metricId));
  return keys.some((k) => {
    if ((pending[k] ?? 0) <= 0) return false;
    const [metricId, entityId, year] = k.split("|");
    if (!metricId || !members.has(metricId) || Number(year) !== col.year) return false;
    return col.aggregate || col.entityId === null || entityId === col.entityId;
  });
}

/** WAI-ARIA treegrid öznitelikleri (satır `<tr>`): `aria-level` 1 tabanlı derinlik. */
export interface TreeAria {
  "aria-level": number;
  "aria-setsize": number;
  "aria-posinset": number;
  "aria-expanded"?: boolean;
}

/**
 * `siblings`: aynı üstün görünür kardeş satırları (sırayla, `row` dahil). `aria-expanded` yalnız
 * açılabilir satırda (kategori bandı / toplam / grup bandı — `open` tanımlı) yazılır.
 */
export function treeAriaFor(
  row: { depth: number; open?: boolean | undefined },
  siblings: readonly object[],
): TreeAria {
  const at = siblings.indexOf(row);
  return treeAria(row, siblings.length, at >= 0 ? at + 1 : 1);
}

function treeAria(
  row: { depth: number; open?: boolean | undefined },
  size: number,
  pos: number,
): TreeAria {
  return {
    "aria-level": row.depth + 1,
    "aria-setsize": size,
    "aria-posinset": pos,
    ...(row.open !== undefined ? { "aria-expanded": row.open } : {}),
  };
}

/** Görünür satırlara treegrid öznitelikleri yazar (kardeşler: aynı üstün görünür satırları). */
export function attachTreeAria<
  R extends { indicator: { parentId?: string | null }; depth: number; open?: boolean | undefined },
>(rows: (R & { tree?: TreeAria })[], known: ReadonlyMap<string, unknown>): void {
  const key = (r: R): string | null =>
    r.indicator.parentId && known.has(r.indicator.parentId) ? r.indicator.parentId : null;
  const siblings = new Map<string | null, R[]>();
  for (const r of rows) {
    const list = siblings.get(key(r)) ?? [];
    list.push(r);
    siblings.set(key(r), list);
  }
  const seen = new Map<string | null, number>();
  for (const r of rows) {
    const k = key(r);
    const pos = (seen.get(k) ?? 0) + 1;
    seen.set(k, pos);
    r.tree = treeAria(r, siblings.get(k)?.length ?? 1, pos);
  }
}
